'use client';

import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useSwrJobItem } from '@hooks/useSwrApi/endpoints/job-operations';
import { useSwrJobInterviewQuestions } from '@hooks/useSwrApi/endpoints/job';
import { Button } from '@components/common';
import JobBasedInterviewEndScreen from '@components/templates/ai-interview/JobBasedInterviewEndScreen';
import { TextInterviewForm } from '@components/templates/ai-interview/text-interview';
import JobAiInterview from './JobAiInterview';
import { InterviewTypeSelector } from '../interview-selection/InterviewTypeSelector';
import { InterviewTypeSwitcher } from './InterviewTypeSwitcher';
import AudioInterview from '../../../audio-interview/AudioInterview';


interface UnifiedJobInterviewProps {
  jobSlug: string;
  locale: Locale;
  serverSideData?: ApiSchema<'InterviewQuestionListResult'>;
  initialType?: 'video' | 'audio' | 'text';
}

type InterviewType = 'video' | 'audio' | 'text';
type InterviewStep = 'type-selection' | 'interview-flow' | 'end-screen';

/**
 * UnifiedJobInterview - A unified interview flow that works for all interview types
 *
 * Flow:
 * 1. User selects interview type (video/audio/text)
 * 2. Fetches list of jobs missing interviews
 * 3. User completes interviews for each job
 * 4. User can skip jobs or change interview type between interviews (not during an interview)
 */
const UnifiedJobInterview = (props: UnifiedJobInterviewProps) => {
  const {
    jobSlug, locale, serverSideData, initialType,
  } = props;
  const { t } = useTranslation('common');

  // State management
  const [currentStep, setCurrentStep] = useState<InterviewStep>(
    initialType ? 'interview-flow' : 'type-selection',
  );
  const [selectedType, setSelectedType] = useState<InterviewType | null>(initialType || null);
  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const [completedJobTitle, setCompletedJobTitle] = useState<string | null>(null);

  // Fetch pending interviews for all types
  const { data: pendingInterviews } = useSwrJobInterviewQuestions({
    locale,
    payload: { sortJobSlug: jobSlug, questionStatus: 'unanswered' },
    serverSideData,
  });

  const jobItems = pendingInterviews?.items || [];
  const jobCount = jobItems.length;
  const currentJobSlug = jobItems[currentJobIndex]?.jobSlug || '';

  // Current job being interviewed
  const { data: currentJob } = useSwrJobItem({
    locale,
    jobSlug: jobItems[currentJobIndex]?.jobSlug || '',
    shouldFetch: !!jobItems[currentJobIndex]?.jobSlug,
  });

  // Next job for end screen preview
  const nextJobIndex = currentJobIndex + 1;
  const nextJobSlug = jobItems[nextJobIndex]?.jobSlug || '';
  const { data: jobItem, isValidating: isLoading } = useSwrJobItem({
    locale,
    jobSlug: nextJobSlug,
    shouldFetch: nextJobSlug !== '',
  });

  // Handlers
  const handleInterviewTypeSelected = (type: InterviewType) => {
    setSelectedType(type);
    setCurrentStep('interview-flow');
  };

  const handleInterviewComplete = () => {
    // Always show end screen to display next available jobs
    setCompletedJobTitle(currentJob?.title || '');
    setCurrentStep('end-screen');
  };

  const handleSkipJob = () => {
    if (currentJobIndex < jobCount - 1) {
      setCurrentJobIndex((prev) => prev + 1);
    }
  };

  const handleStartNextInterview = () => {
    setCurrentJobIndex((prev) => prev + 1);
    setCurrentStep('interview-flow');
  };

  const handleSwitchInterviewType = (type: InterviewType) => {
    // Switch to a different interview type
    setSelectedType(type);
    setCurrentStep('interview-flow');
  };

  const currentJobQuestions = jobItems[currentJobIndex]?.questions || [];

  // Step 1: Interview Type Selection
  if (currentStep === 'type-selection') {
    return (
      <InterviewTypeSelector
        onInterviewTypeSelected={handleInterviewTypeSelected}
      />
    );
  }

  // Step 2: Interview Flow
  if (currentStep === 'interview-flow' && selectedType) {
    switch (selectedType) {
      case 'video':
        return (
          <div className="flex flex-col">
            <JobAiInterview
              jobSlug={currentJobSlug}
              locale={locale}
              questions={currentJobQuestions}
              onComplete={handleInterviewComplete}
            />
            <InterviewTypeSwitcher
              currentType="video"
              onSwitchType={handleSwitchInterviewType}
              className="mt-8 border-t border-surface-200 pt-6"
            />
          </div>
        );

      case 'audio':
        return (
          <div className="flex flex-col">
            <AudioInterview
              slug={currentJobSlug}
              locale={locale}
              scope="job"
              onComplete={handleInterviewComplete}
            />
            <InterviewTypeSwitcher
              currentType="audio"
              onSwitchType={handleSwitchInterviewType}
              className="mt-8 border-t border-surface-200 pt-6"
            />
          </div>
        );

      case 'text':
        return (
          <div className="flex flex-col">
            <TextInterviewForm
              jobQuestion={currentJobQuestions}
              locale={locale}
              onSaveSuccess={handleInterviewComplete}
            />
            <InterviewTypeSwitcher
              currentType="text"
              onSwitchType={handleSwitchInterviewType}
              className="mt-8 border-t border-surface-200 pt-6"
            />
          </div>
        );

      default:
        return null;
    }
  }

  // Step 3: End Screen
  if (currentStep === 'end-screen' && selectedType && completedJobTitle) {
    // nextJobIndex points to the job being previewed on the end screen
    const isLastJobInList = nextJobIndex >= jobCount - 1;

    return (
      <div className="flex flex-col items-center space-y-4 p-6">
        <h2 className="mb-6 flex items-center gap-2 text-2xl font-bold">
          <CheckCircleIcon className="size-6 text-secondary" />
          {t('job.video.interview.success.message', {
            jobTitle: completedJobTitle,
          })}
        </h2>

        {/* End Screen with actions */}
        <JobBasedInterviewEndScreen
          job={jobItem}
          isLoading={isLoading}
          startInterviewAction={(
            <Button
              styling="solid"
              color="secondary"
              rounding="full"
              className="flex-1 gap-2"
              onClick={handleStartNextInterview}
            >
              {t('ai.interview.start')}
            </Button>
          )}
          skipAction={
            !isLastJobInList ? (
              <Button
                styling="text"
                color="primary"
                className="flex-1 font-bold !no-underline"
                onClick={handleSkipJob}
              >
                {t('job.video.interview.skip')}
              </Button>
            ) : null
          }
        />
      </div>
    );
  }

  return null;
};

export default UnifiedJobInterview;

